/** Compact WBS-leaf LEVEL pill: shows the leaf's effective Takt_C ("LVL3"),
 *  warns (⚠) when some members are mis-coded, and opens a small menu of the
 *  section's Takt_C values (plus free-type) to override it. Used by BOTH the
 *  board lane header and the scope-ledger leaf header — presentational only.
 *
 *  Variants:
 *    default — the level matches (or was set explicitly)
 *    warn    — miscodeCount > 0 ("coded X but filed as Y")
 *    unset   — no derivable level (dashed "set level" empty state) */
import React from "react";
import { createPortal } from "react-dom";
import "./LevelPill.css";

interface Props {
  /** The effective Takt_C ("LVL3"), or null when nothing parsed and nothing set. */
  level: string | null;
  /** Some members carry a different takt_c — flags the pill. */
  miscodeCount?: number;
  /** Section Takt_C values to choose from. */
  menu: string[];
  readOnly?: boolean;
  onSet: (level: string) => void;
}

const LevelPill: React.FC<Props> = ({ level, miscodeCount = 0, menu, readOnly, onSet }) => {
  const [open, setOpen] = React.useState(false);
  const [pos, setPos] = React.useState<{ left: number; top: number } | null>(null);
  const [draft, setDraft] = React.useState("");
  const btnRef = React.useRef<HTMLButtonElement | null>(null);
  const menuRef = React.useRef<HTMLDivElement | null>(null);

  React.useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    const r = btnRef.current?.getBoundingClientRect();
    if (r) setPos({ left: Math.min(r.left, window.innerWidth - 170), top: r.bottom + 2 });
  }, [open]);

  React.useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (btnRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    window.addEventListener("mousedown", onDown);
    return () => window.removeEventListener("mousedown", onDown);
  }, [open]);

  const warn = miscodeCount > 0;
  const unset = !level;
  const cls =
    "level-pill" +
    (warn ? " level-pill--warn" : "") +
    (unset ? " level-pill--unset" : "");

  const pick = (v: string) => {
    const value = v.trim();
    if (value) onSet(value);
    setOpen(false);
    setDraft("");
  };

  // Menu choices: the section's Takt_C values, de-duped, with the current
  // effective level pinned even when the menu doesn't list it.
  const choices = React.useMemo(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const c of [...(level ? [level] : []), ...menu]) {
      const v = (c || "").trim();
      if (!v || seen.has(v.toUpperCase())) continue;
      seen.add(v.toUpperCase());
      out.push(v);
    }
    return out;
  }, [level, menu]);

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        className={cls}
        disabled={readOnly}
        title={
          warn
            ? `${miscodeCount} activit${miscodeCount === 1 ? "y" : "ies"} mis-coded — click to set the leaf level`
            : unset
              ? "No level detected — click to set"
              : "Leaf Takt level — click to change"
        }
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation();
          if (readOnly) return;
          setOpen((o) => !o);
        }}
      >
        {unset ? "set level" : level}
        {warn && <span className="level-pill-warn-mark" aria-hidden="true"> ⚠</span>}
      </button>
      {open &&
        pos &&
        createPortal(
          <div className="level-pill-menu" ref={menuRef} style={{ left: pos.left, top: pos.top }}>
            {choices.map((c) => (
              <button
                key={c}
                type="button"
                className={
                  "level-pill-menu-item" +
                  (level && c.toUpperCase() === level.toUpperCase()
                    ? " level-pill-menu-item--active"
                    : "")
                }
                onMouseDown={(e) => e.stopPropagation()}
                onClick={() => pick(c)}
              >
                {c}
              </button>
            ))}
            <div className="level-pill-menu-new">
              <input
                type="text"
                value={draft}
                placeholder="New level…"
                onMouseDown={(e) => e.stopPropagation()}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") pick(draft);
                  if (e.key === "Escape") setOpen(false);
                }}
              />
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};

export default LevelPill;
